import { useEffect, useState, useMemo, useCallback } from "react";
import { Schedule } from "screens/tasks/components";
import { useTasks } from "shared/contexts/tasks.context";
import { Task, TaskPhase, TaskPriority, TaskLabel } from "shared/types/task";
import ViewTaskModal from "shared/components/viewTaskModal/viewTaskModal";
import { AddTaskModal } from "shared/components";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Button } from "@heroui/react";
import { Plus } from "@phosphor-icons/react";
import { TasksSection } from "./components/TasksSection";
import { FilterBar } from 'shared/components/filterBar';
import type { FilterSection } from 'shared/components/filterBar';
import { availablePhases as phaseOptions, getColumnForPhase } from './taskPhaseConfig';
// import { Task as TaskType } from "shared/types/task";


const Tasks = () => {
  const { tasks: realTasks, refreshTasks } = useTasks();
  const navigate = useNavigate();
  const { user } = useUser();
  
  const tasks: Task[] = realTasks;
  
  const [selectedFilter, setSelectedFilter] = useState("myTasks");
  const [specificUserId, setSpecificUserId] = useState<string | null>(null);
  const [showViewTaskModal, setShowViewTaskModal] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [createTaskPhase, setCreateTaskPhase] = useState<TaskPhase>(TaskPhase.INBOX);
  const { taskId } = useParams<{ taskId?: string }>();
  
  // FilterBar states
  const [filterBarSearchText, setFilterBarSearchText] = useState('');
  const [phaseFilters, setPhaseFilters] = useState<string[]>([]);
  const [priorityFilters, setPriorityFilters] = useState<string[]>([]);
  const [labelFilters, setLabelFilters] = useState<string[]>([]);

  useEffect(() => {
    if (tasks.length > 0 && taskId) {
      const task = tasks.find((t: any) => t.id == taskId);
      if (task) {
        setSelectedTask(task);
        setShowViewTaskModal(true);
      } else {
        navigate("/tasks");
      }
    }
  }, [tasks, taskId]);

  useEffect(() => {
    if (user) setSpecificUserId(user.id);
  }, [user]);

  // Listen for custom event from NavMenu when "Taak" is clicked while on tasks page
  useEffect(() => {
    const handleOpenTaskModal = () => {
      setCreateTaskPhase(TaskPhase.INBOX);
      setShowCreateTaskModal(true);
    };

    window.addEventListener('openTaskModal', handleOpenTaskModal);

    return () => {
      window.removeEventListener('openTaskModal', handleOpenTaskModal);
    };
  }, []);

  const setFilter = (value: string) => {
    setSelectedFilter(value);
  };

  const handleCreateTask = (e: any) => {
    e.preventDefault();
    setCreateTaskPhase(TaskPhase.INBOX);
    setShowCreateTaskModal(true);
  };

  const handleCreateTaskWithPhase = (phase: TaskPhase) => {
    setCreateTaskPhase(phase);
    setShowCreateTaskModal(true);
  };

  // FilterBar available options (imported from config)
  const availablePhases = useMemo(() => phaseOptions, []);

  const availablePriorities = useMemo(() => [
    { key: TaskPriority.LOW.toString(), label: 'Laag' },
    { key: TaskPriority.MEDIUM.toString(), label: 'Normaal' },
    { key: TaskPriority.HIGH.toString(), label: 'Hoog' },
    { key: TaskPriority.URGENT.toString(), label: 'Urgent' },
  ], []);

  const availableLabels = useMemo(() => Object.values(TaskLabel).map(label => ({
    key: label,
    label: label.charAt(0).toUpperCase() + label.slice(1)
  })), []);

  // FilterBar update function
  const updateFilters = useCallback((key: string, values: string[]) => {
    switch (key) {
      case 'phase':
        setPhaseFilters(values);
        break;
      case 'priority':
        setPriorityFilters(values);
        break;
      case 'label':
        setLabelFilters(values);
        break;
    }
  }, []);

  // FilterBar sections configuration
  const filterSections: FilterSection[] = useMemo(() => [
    {
      key: 'phase',
      label: 'Fase',
      options: availablePhases,
      selectedValues: phaseFilters,
      onUpdate: (values) => updateFilters('phase', values),
      color: 'moss' as const,
    },
    {
      key: 'priority',
      label: 'Prioriteit',
      options: availablePriorities,
      selectedValues: priorityFilters,
      onUpdate: (values) => updateFilters('priority', values),
      color: 'brick' as const,
    },
    {
      key: 'label',
      label: 'Labels',
      options: availableLabels,
      selectedValues: labelFilters,
      onUpdate: (values) => updateFilters('label', values),
      color: 'neutral' as const,
    },
  ], [availablePhases, availablePriorities, availableLabels, phaseFilters, priorityFilters, labelFilters, updateFilters]);

  const getFilteredTasks = () => {
    let filtered: Task[] = [];
    
    // First apply user filter
    switch (selectedFilter) {
      case "myTasks":
        filtered = tasks.filter((task) => task.assignee === specificUserId);
        break;
      case "delegatedTasks":
        filtered = tasks.filter((task) => task.assignedBy === specificUserId);
        break;
      case "allTasks":
        filtered = tasks.filter(
          (task) =>
            task.assignedBy === specificUserId ||
            task.assignee === specificUserId,
        );
        break;
      default:
        filtered = tasks;
    }

    // Apply FilterBar filters
    return applyFilterBarFilters(filtered);
  };

  // FilterBar filtering logic
  const applyFilterBarFilters = useCallback((tasksToFilter: Task[]) => {
    if (!tasksToFilter?.length) return [];
    
    return tasksToFilter.filter((task) => {
      // Phase filter
      if (phaseFilters.length > 0) {
        const taskColumn = getColumnForPhase(task.phase);
        if (!phaseFilters.includes(taskColumn)) {
          return false;
        }
      }

      // Priority filter
      if (priorityFilters.length > 0) {
        if (!priorityFilters.includes(task.priority.toString())) {
          return false;
        }
      }

      // Label filter
      if (labelFilters.length > 0) {
        const taskLabelNames = (task.labels || []).map(l => String(typeof l === 'string' ? l : l.name));
        const hasSelectedLabel = labelFilters.some(filter => taskLabelNames.includes(String(filter)));
        if (!hasSelectedLabel) return false;
      }

      // Search filter
      const term = filterBarSearchText.trim().toLowerCase();
      if (term) {
        const matches = (
          task.title?.toLowerCase().includes(term) ||
          task.description?.toLowerCase().includes(term)
        );
        
        if (!matches) return false;
      }

      return true;
    });
  }, [filterBarSearchText, phaseFilters, priorityFilters, labelFilters]);

  // FilterBar filter chips
  const filterBarChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      category?: string;
      color?: 'moss' | 'brick' | 'yellow' | 'neutral';
      onRemove: () => void;
    }> = [];
    
    // Phase filter chips
    phaseFilters.forEach(phase => {
      const phaseOption = availablePhases.find(p => p.key === phase);
      if (phaseOption) {
        chips.push({
          key: `phase-${phase}`,
          label: phaseOption.label,
          category: 'Fase',
          color: 'moss' as const,
          onRemove: () => setPhaseFilters(prev => prev.filter(p => p !== phase))
        });
      }
    });
    
    // Priority filter chips
    priorityFilters.forEach(priority => {
      const priorityOption = availablePriorities.find(p => p.key === priority);
      if (priorityOption) {
        chips.push({
          key: `priority-${priority}`,
          label: priorityOption.label,
          category: 'Prioriteit',
          color: 'brick' as const,
          onRemove: () => setPriorityFilters(prev => prev.filter(p => p !== priority))
        });
      }
    });
    
    // Label filter chips
    labelFilters.forEach(label => {
      const labelOption = availableLabels.find(l => l.key === label);
      if (labelOption) {
        chips.push({
          key: `label-${label}`,
          label: labelOption.label,
          category: 'Label',
          color: 'neutral' as const,
          onRemove: () => setLabelFilters(prev => prev.filter(l => l !== label))
        });
      }
    });
    
    return chips;
  }, [phaseFilters, priorityFilters, labelFilters, availablePhases, availablePriorities, availableLabels]);


  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Taken &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="mb-10 flex flex-col gap-8">
        <header className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <Schedule
              specificUserId={specificUserId}
              setSpecificUserId={setSpecificUserId}
              onSelectFilter={(value) => setFilter(value as string)}
            />
            <Button
              color="primary"
              startContent={<Plus weight="duotone" size={18} />}
              onClick={handleCreateTask}
              className="bg-moss-primary"
            >
              Taak toevoegen
            </Button>
          </div>
        </header>

        {/* Kanban Board Section with all phases including Inbox */}
        <TasksSection
          tasks={getFilteredTasks()}
          loading={false}
          onUpdate={refreshTasks}
          onTaskClick={(task: Task) => {
            setSelectedTask(task);
            setShowViewTaskModal(true);
          }}
          onAddClick={handleCreateTaskWithPhase}
        />

        {/* View Task Modal */}
        {showViewTaskModal && (
          <ViewTaskModal
            task={selectedTask}
            isOpen={showViewTaskModal}
            onOpenChange={setShowViewTaskModal}
            onClose={() => {
              setShowViewTaskModal(false);
              navigate("/tasks");
            }}
          />
        )}

        {/* Add Task Modal */}
        <AddTaskModal
          phase={createTaskPhase}
          isOpen={showCreateTaskModal}
          onOpenChange={setShowCreateTaskModal}
          onClose={() => setShowCreateTaskModal(false)}
        />
      </div>

      {/* FilterBar at parent level */}
      <FilterBar
        searchValue={filterBarSearchText}
        onSearchChange={setFilterBarSearchText}
        searchPlaceholder="Zoek taken op titel, beschrijving..."
        filterChips={filterBarChips}
        filterSections={filterSections}
        showWhenEmpty={true}
        showSpacer={false}
      />
    </>
  );
};

export default Tasks;
